import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, RefreshCw, X, Clock } from 'lucide-react';
import { useErrorRecovery } from '@/hooks/useErrorRecovery';
import { format } from 'date-fns';

export const ErrorRecoveryPanel = () => {
  const { errors, isRecovering, executeRecovery, clearError, clearAllErrors, hasUnresolvedErrors } = useErrorRecovery();
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  const toggleExpanded = (errorId: string) => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(errorId)) {
        newSet.delete(errorId);
      } else {
        newSet.add(errorId);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      case 'low': return <CheckCircle className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (errors.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-muted-foreground">No recent errors</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Error Recovery Center
              </CardTitle>
              <CardDescription>
                {errors.length} error{errors.length !== 1 ? 's' : ''} detected
                {hasUnresolvedErrors && ' - Some require attention'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {hasUnresolvedErrors && (
                <Badge variant="destructive">Action Required</Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllErrors}
                disabled={isRecovering}
              >
                Clear All
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Error List */}
      <div className="space-y-3">
        {errors.map((error) => (
          <Card key={error.id} className={error.resolved ? 'opacity-60' : ''}>
            <Collapsible>
              <CollapsibleTrigger
                className="w-full"
                onClick={() => toggleExpanded(error.id)}
              >
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedErrors.has(error.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <div className="text-left">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          {error.resolved ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          )}
                          {error.context.component} - {error.context.action}
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {error.message}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={error.resolved ? 'default' : 'secondary'}>
                        {error.resolved ? 'Resolved' : 'Active'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(error.timestamp, 'HH:mm:ss')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearError(error.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-4">
                    {/* Error Details */}
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Error Details</AlertTitle>
                      <AlertDescription>
                        <div className="mt-2 space-y-1 text-sm">
                          <div><strong>Component:</strong> {error.context.component}</div>
                          <div><strong>Action:</strong> {error.context.action}</div>
                          <div><strong>Time:</strong> {format(error.timestamp, 'PPpp')}</div>
                          {error.context.itemId && (
                            <div><strong>Item ID:</strong> {error.context.itemId}</div>
                          )}
                          {error.context.additionalData && (
                            <div>
                              <strong>Additional Data:</strong>
                              <pre className="mt-1 text-xs bg-muted p-2 rounded">
                                {JSON.stringify(error.context.additionalData, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>

                    {/* Recovery Actions */}
                    {error.recoveryActions.length > 0 && !error.resolved && (
                      <div>
                        <h4 className="text-sm font-medium mb-3">Available Recovery Actions</h4>
                        <div className="grid gap-2">
                          {error.recoveryActions.map((action) => (
                            <Card key={action.id} className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Badge variant={getSeverityColor(action.severity)} className="flex items-center gap-1">
                                    {getSeverityIcon(action.severity)}
                                    {action.severity}
                                  </Badge>
                                  <div>
                                    <div className="font-medium text-sm">{action.label}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {action.description}
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => executeRecovery(error.id, action.id)}
                                  disabled={isRecovering}
                                >
                                  {isRecovering ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    'Execute'
                                  )}
                                </Button>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {error.resolved && (
                      <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertTitle>Error Resolved</AlertTitle>
                        <AlertDescription>
                          This error has been successfully resolved through recovery actions.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>
    </div>
  );
};